import { useMutation } from 'react-query';
import { Endpoints } from '../utils/endpoints.ts';
import { useEffect, useRef } from 'react';
import { openNotification } from '../components';
import { useTranslation } from 'react-i18next';
import { apiDocumentServiceRepository } from 'services/repositories/implementations/ApiDocumentServiceRepository.ts';

type QueryResultSuccess = {
  pdfLink: string;
};
type QueryResultError = {
  title?: string;
  path?: string;
  message?: string;
};
type QueryParameters = {
  contentId: string;
  documentPreviewUrl: string;
};

const useGeneratePdfFromContentMutation = () => {
  return useMutation<QueryResultSuccess, QueryResultError, QueryParameters>({
    retry: 1,
    retryDelay: 100,
    mutationFn: ({ contentId, documentPreviewUrl }) => {
      return apiDocumentServiceRepository.contentPdfExport(contentId, documentPreviewUrl);
    },
  });
};

type GeneratePdf = {
  contentId: string;
  documentId: string;
  fileName: string;
};

const useGeneratePdfFromContent = () => {
  const { t } = useTranslation();
  const pdfFileName = useRef<string>('Untitled Document');
  const { isLoading, mutate, isError, error, data, status } = useGeneratePdfFromContentMutation();

  const generatePdf = async ({ contentId, documentId, fileName }: GeneratePdf) => {
    pdfFileName.current = fileName;
    const baseUrl = `${window.location.origin}`;
    const documentPreviewUrl = baseUrl + Endpoints.getDocumentPreviewPage(documentId);

    return mutate({ contentId, documentPreviewUrl });
  };

  const showErrorNotification = (error?: string) => {
    openNotification({
      type: 'error',
      title: t('share_document_v3.share_card.download_as_pdf_error'),
      description: error,
    });
  };

  useEffect(() => {
    if (isError) {
      showErrorNotification(error?.message);
    }
  }, [isError]);

  useEffect(() => {
    if (status === 'success') {
      if (!data || !data.pdfLink) {
        showErrorNotification();
        return;
      }

      const link = document.createElement('a');
      link.href = data.pdfLink;
      link.download = `${pdfFileName.current}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [status]);

  return {
    generatePdf,
    isLoading,
  };
};

export default useGeneratePdfFromContent;
